import {
  ArrowRightStartOnRectangleIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  EllipsisHorizontalCircleIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Link, NavLink } from 'react-router-dom';

import { useScrimClose } from '../../hooks/useScrimClose';
import { haptic } from '../../lib/haptics';
import {
  formatBadge,
  navigationFor,
  TAB_BAR_MORE_LABEL,
  type NavBadge,
  type Navigation,
} from '../../lib/navigation';
import { BRAND_MARK } from '../GreetingOverlay';

const Badge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="tabbar-badge" aria-label={`${count} neprečítaných`}>
      {formatBadge(count)}
    </span>
  ) : null;

/**
 * Spodná lišta na telefóne: štyri ciele a „Ďalšie“ (pozri lib/navigation.ts,
 * prečo nie „Viac“). Každá položka má aspoň 44 px na výšku aj na šírku.
 *
 * Pri otvorenej klávesnici odíde dole — natívne appky ju nechávajú klávesnicou
 * prekryť a lišta plávajúca nad klávesnicou berie riadok písania.
 */
export const MobileTabBar = ({
  counts,
  hasAppUpdate,
  keyboardOpen,
  moreOpen,
  onOpenMore,
  navigation = navigationFor(undefined),
}: {
  counts: Record<NavBadge, number>;
  hasAppUpdate: boolean;
  keyboardOpen: boolean;
  moreOpen: boolean;
  onOpenMore: () => void;
  navigation?: Navigation;
}) => {
  // Schránka nemá v lište vlastné miesto, takže jej počet nesie „Ďalšie“.
  const moreCount = navigation.moreSheet.reduce((sum, item) => sum + (item.badge ? counts[item.badge] : 0), 0);

  return (
    <nav
      aria-label="Hlavná navigácia"
      className={clsx(
        'tabbar pb-safe fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t hairline px-1 pt-1 transition-all duration-200 md:hidden',
        keyboardOpen && 'pointer-events-none translate-y-full opacity-0',
      )}
    >
      {navigation.tabBar.map((item) => {
        const Icon = item.icon;
        const count = item.badge ? counts[item.badge] : 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={() => haptic()}
            className={({ isActive }) => clsx('tabbar-item', isActive && 'is-active')}
          >
            {({ isActive }) => (
              <>
                {/* Kľúč podľa isActive: ikona sa pri aktivácii znova pripojí a
                    krátke „pop“ sa prehrá (raz, nie dookola). */}
                <span key={String(isActive)} className={clsx('relative', isActive && 'tab-pop')}>
                  <Icon className="h-[22px] w-[22px]" aria-hidden="true" />
                  <Badge count={count} />
                </span>
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
      <button
        type="button"
        onClick={onOpenMore}
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        className={clsx('tabbar-item', moreOpen && 'is-active')}
      >
        <span className="relative">
          <EllipsisHorizontalCircleIcon className="h-[22px] w-[22px]" aria-hidden="true" />
          {/* Dva rôzne signály, dva rohy. Keby si delili jedno miesto, správa,
              ktorá čaká, by prekryla novú verziu — alebo naopak. */}
          <Badge count={moreCount} />
          {hasAppUpdate ? (
            <span className="absolute -bottom-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-surface" />
          ) : null}
        </span>
        <span>{TAB_BAR_MORE_LABEL}</span>
      </button>
    </nav>
  );
};

/** List „Ďalšie“ (telefón): zvyšok navigácie, JARVIS a veci účtu. */
export const MoreSheet = ({
  counts,
  hasAppUpdate,
  theme,
  onClose,
  onToggleTheme,
  onOpenJarvis,
  onLogout,
  navigation = navigationFor(undefined),
}: {
  counts: Record<NavBadge, number>;
  hasAppUpdate: boolean;
  theme: 'light' | 'dark';
  onClose: () => void;
  onToggleTheme: () => void;
  onOpenJarvis: () => void;
  onLogout: () => void;
  navigation?: Navigation;
}) => {
  const scrim = useScrimClose(onClose);
  return (
    <div className="sheet-scrim md:hidden" role="presentation" {...scrim}>
      <div
        className="sheet max-h-[85dvh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={TAB_BAR_MORE_LABEL}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-black/10 dark:bg-white/15" />
        {navigation.moreSheet.length > 0 || navigation.jarvis ? (
        <>
        <p className="section-label">Prejsť na</p>
        <div className="list-group">
          {navigation.moreSheet.map((item) => {
            const count = item.badge ? counts[item.badge] : 0;
            return (
              <Link key={item.to} to={item.to} className="list-row list-row-tappable min-h-[48px]" onClick={onClose}>
                <item.icon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                <span className="flex-1 text-[15px] font-medium">{item.label}</span>
                {count > 0 ? <span className="sidebar-badge">{formatBadge(count)}</span> : null}
                <ChevronRightIcon className="h-4 w-4 text-slate-300 dark:text-slate-600" aria-hidden="true" />
              </Link>
            );
          })}
          {navigation.jarvis ? (
          <button
            type="button"
            className="list-row list-row-tappable min-h-[48px] w-full text-left"
            onClick={() => {
              onClose();
              onOpenJarvis();
            }}
          >
            <img src={BRAND_MARK} alt="" aria-hidden="true" className="h-5 w-5 rounded-full object-contain" />
            <span className="flex-1 text-[15px] font-medium">
              JARVIS <span className="beta-chip ml-1">beta</span>
            </span>
            <ChevronRightIcon className="h-4 w-4 text-slate-300 dark:text-slate-600" aria-hidden="true" />
          </button>
          ) : null}
        </div>
        </>
        ) : null}

        <p className={clsx('section-label', (navigation.moreSheet.length > 0 || navigation.jarvis) && 'mt-5')}>Účet</p>
        <div className="list-group">
          <Link to="/settings" className="list-row list-row-tappable min-h-[48px]" onClick={onClose}>
            <Cog6ToothIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            <span className="flex-1 text-[15px] font-medium">Nastavenia</span>
            {hasAppUpdate ? (
              <span className="h-2.5 w-2.5 rounded-full bg-red-500" aria-label="Aktualizácia aplikácie je k dispozícii" />
            ) : null}
            <ChevronRightIcon className="h-4 w-4 text-slate-300 dark:text-slate-600" aria-hidden="true" />
          </Link>
          <button type="button" className="list-row list-row-tappable min-h-[48px] w-full text-left" onClick={onToggleTheme}>
            {theme === 'dark' ? (
              <SunIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            ) : (
              <MoonIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
            )}
            <span className="flex-1 text-[15px] font-medium">{theme === 'dark' ? 'Svetlý režim' : 'Tmavý režim'}</span>
          </button>
          <button
            type="button"
            className="list-row list-row-tappable min-h-[48px] w-full text-left text-red-600 dark:text-red-400"
            onClick={onLogout}
          >
            <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
            <span className="flex-1 text-[15px] font-medium">Odhlásiť sa</span>
          </button>
        </div>
      </div>
    </div>
  );
};
